/* eslint-disable max-lines */
import React, { forwardRef } from 'react';
import { Bar } from 'react-chartjs-2';
import { ChartOptions, Plugin } from 'chart.js';
import { useTheme } from 'providers/ThemeProvider';

type Value = { x: string; y: number; formattedY: string; percentage?: number; barTotal?: number };

export interface StackedBarChartProps {
  datasets: {
    data: Value[];
    label: string;
    backgroundColor?: string;
  }[];
  selectedDatasetName?: string;
  colors: string[];
}

const StackedBarChart = forwardRef<any, StackedBarChartProps>(({ datasets, colors }, ref) => {
  const { theme } = useTheme();
  const isDarkMode = theme.colorPalette.isDarkMode;

  // Calculate the number of bars (data points)
  const numBars = datasets[0]?.data.length || 0;
  const barSpacing = 70; // Same as in barPositionPlugin
  const minChartWidth = numBars * barSpacing + 100; // Add padding

  // Plugin to add spacing between legend and chart
  const legendSpacingPlugin: Plugin<'bar'> = {
    id: 'legendSpacing',
    beforeInit(chart: any) {
      const originalFit = chart.legend.fit;
      chart.legend.fit = function fit() {
        originalFit.call(this);
        this.height += 30;
      };
    },
  };

  const data = {
    datasets: datasets.map((dataset, index) => {
      return {
        ...dataset,
        backgroundColor: colors[index],
        data: dataset.data.map((value) => {
          const barTotal = datasets.reduce((sum, dataset) => {
            const matchingValue = dataset.data.find(({ x }) => x === value.x);
            return sum + (matchingValue ? Number(matchingValue.y) : 0);
          }, 0);

          const percentage = barTotal > 0 ? (Number(value.y) / barTotal) * 100 : 0;

          return {
            ...value,
            barTotal: parseFloat(barTotal.toFixed(2)),
            percentage: parseFloat(percentage.toFixed(2)),
          };
        }),
      };
    }),
    labels: datasets[0]?.data.map((item) => item.x) || [],
  };

  // Custom plugin to position bars on the left
  const barPositionPlugin: Plugin<'bar'> = {
    id: 'barPosition',
    beforeDatasetsDraw(chart) {
      const {
        data,
        chartArea: { left },
      } = chart;

      if (data.datasets.length === 0) return;

      // Position all stacked bars from the left
      data.datasets.forEach((_, datasetIndex) => {
        chart.getDatasetMeta(datasetIndex).data.forEach((dataPoint: any, index) => {
          dataPoint.x = left + 70 * index + 40; // 70px spacing between bar groups
          dataPoint.width = 60;
        });
      });
    },
    afterDraw(chart) {
      const {
        ctx,
        data,
        chartArea: { left, bottom },
      } = chart;

      if (data.datasets.length === 0 || !data.labels) return;

      ctx.save();
      ctx.font = `9px ${theme.fontFamily}`;
      ctx.fillStyle = isDarkMode ? theme.colorPalette.grey.grey9 : theme.colorPalette.grey.grey7;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';

      // Draw labels under each bar
      data.labels.forEach((label, index) => {
        const labelX = left + 70 * index + 40;
        const labelY = bottom + 5;

        ctx.fillText(label as string, labelX, labelY);
      });

      ctx.restore();
    },
  };

  const options: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    layout: {
      padding: {
        top: 60,
        bottom: 25,
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: {
          display: false,
        },
        ticks: {
          display: false, // Hide default ticks since we're drawing custom labels
        },
      },
      y: {
        stacked: true,
        grid: {
          color: isDarkMode ? theme.colorPalette.grey.grey4 : theme.colorPalette.grey.grey3,
        },
        ticks: {
          font: {
            size: 9,
            family: theme.fontFamily,
            weight: 'lighter',
          },
          color: isDarkMode ? theme.colorPalette.grey.grey9 : theme.colorPalette.grey.grey7,
        },
      },
    },
    plugins: {
      legend: {
        align: 'start',
        labels: {
          boxWidth: 20,
          boxHeight: 10,
          padding: 15,
          font: {
            size: 13,
            family: theme.fontFamily,
            weight: 'lighter',
          },
          color: isDarkMode ? theme.colorPalette.grey.grey9 : theme.colorPalette.grey.grey7,
        },
      },
      title: {
        display: false,
      },
      tooltip: {
        backgroundColor: isDarkMode ? theme.colorPalette.grey.grey2 : theme.colorPalette.white,
        titleColor: isDarkMode ? theme.colorPalette.grey.grey9 : theme.colorPalette.grey.grey7,
        bodyColor: isDarkMode ? theme.colorPalette.grey.grey9 : theme.colorPalette.grey.grey7,
        borderColor: isDarkMode ? theme.colorPalette.grey.grey4 : theme.colorPalette.grey.grey3,
        borderWidth: 1,
        padding: 12,
        displayColors: true,
        boxWidth: 15,
        boxHeight: 15,
        usePointStyle: false,
        titleFont: {
          size: 13,
          family: theme.fontFamily,
          weight: 'bold',
        },
        bodyFont: {
          size: 12,
          family: theme.fontFamily,
          weight: 'normal',
        },
        footerFont: {
          size: 12,
          family: theme.fontFamily,
          weight: 'bold',
        },
        footerColor: isDarkMode ? theme.colorPalette.grey.grey9 : theme.colorPalette.grey.grey7,
        filter: (tooltipItem) => {
          const dataPoint = (tooltipItem.dataset.data as unknown as Value[])[tooltipItem.dataIndex];
          return dataPoint && dataPoint.y !== 0 && dataPoint.y !== null;
        },
        callbacks: {
          title: (tooltipItems) => {
            if (tooltipItems.length > 0) {
              const dataPoint = (tooltipItems[0].dataset.data as unknown as Value[])[tooltipItems[0].dataIndex];
              return dataPoint.x;
            }
            return '';
          },
          label: (context) => {
            const value = (context.dataset.data as unknown as Value[])[context.dataIndex];
            const dataset = context.dataset;
            return `${dataset.label}       ${value.formattedY}`;
          },
          footer: (tooltipItems) => {
            let sum = 0;
            tooltipItems.forEach((tooltipItem) => {
              const value = (tooltipItem.dataset.data as unknown as Value[])[tooltipItem.dataIndex];
              sum += value.y;
            });
            return `Total:       ${sum.toLocaleString()}`;
          },
        },
      },
    },
  };

  const dataLabelsPlugin = {
    id: 'dataLabels',
    afterDatasetsDraw(chart: any) {
      const { ctx } = chart;

      const fontSize = 12;

      ctx.save();
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = isDarkMode ? theme.colorPalette.grey.grey5 : theme.colorPalette.grey.grey8;
      ctx.font = `${fontSize}px ${theme.fontFamily}`;

      chart.data.datasets.forEach((dataset: { data: Value[] }, datasetIndex: number) => {
        const meta = chart.getDatasetMeta(datasetIndex);

        // Skip hidden datasets
        if (meta.hidden) return;

        meta.data.forEach((bar: { height: number; x: number; y: number }, index: number) => {
          const value = dataset.data[index];
          if (!value) return;

          if (!value.y || value.y === 0) return;

          const barHeight = bar.height || 0;
          const minHeightForLabel = fontSize * 2.5;

          if (barHeight < minHeightForLabel) return;

          const x = bar.x;
          const y = bar.y + bar.height / 2;

          // Calculate dynamic percentage based on visible datasets only
          let visibleTotal = 0;
          chart.data.datasets.forEach((ds: { data: Value[] }, idx: number) => {
            const dsMeta = chart.getDatasetMeta(idx);
            if (!dsMeta.hidden) {
              const dataPoint = ds.data[index];
              if (dataPoint && typeof dataPoint.y === 'number') {
                visibleTotal += dataPoint.y;
              }
            }
          });

          const percentage = visibleTotal > 0 ? ((value.y / visibleTotal) * 100).toFixed(2) : '0.00';
          const labels = [`${value.formattedY}`, `(${percentage}%)`];

          const totalHeight = labels.length * fontSize;
          let startY = y - totalHeight / 2 + fontSize / 2;

          labels.forEach((labelLine: string) => {
            ctx.fillText(labelLine, x, startY);
            startY += fontSize;
          });
        });
      });

      ctx.restore();
    },
  };

  const totalsPlugin = {
    id: 'totals',
    afterDatasetsDraw(chart: any) {
      const datasets = chart.data.datasets;
      const ctx = chart.ctx;

      ctx.save();
      ctx.font = `14px ${theme.fontFamily}`;
      ctx.fillStyle = isDarkMode ? theme.colorPalette.grey.grey9 : theme.colorPalette.grey.grey7;
      ctx.textAlign = 'center';

      // Get the first visible dataset's meta to access bar positions
      const firstVisibleMeta = datasets.reduce((found: any, _: any, index: number) => {
        if (found) return found;
        const meta = chart.getDatasetMeta(index);
        return !meta.hidden ? meta : null;
      }, null);

      if (!firstVisibleMeta) {
        ctx.restore();
        return;
      }

      firstVisibleMeta.data.forEach((datapoint: { x: number; y: number }, index: number) => {
        // Calculate total for this bar position from all visible datasets
        let total = 0;
        datasets.forEach((dataset: { data: Value[] }, datasetIndex: number) => {
          const meta = chart.getDatasetMeta(datasetIndex);
          if (!meta.hidden) {
            const value = dataset.data[index];
            if (value && typeof value.y === 'number') {
              total += value.y;
            }
          }
        });

        // Position the total at the top of the stacked bar
        const topY = chart.getDatasetMeta(datasets.length - 1).data[index]?.y || datapoint.y;
        ctx.fillText(total.toFixed(2), datapoint.x, topY - 10);
      });

      ctx.restore();
    },
  };

  return (
    <div style={{ width: '100%', overflowX: 'auto', height: '100%' }}>
      <div style={{ minWidth: `${minChartWidth}px`, height: '100%' }}>
        <Bar
          ref={ref}
          data={data}
          options={options}
          plugins={[legendSpacingPlugin, barPositionPlugin, totalsPlugin, dataLabelsPlugin]}
        />
      </div>
    </div>
  );
});

export default StackedBarChart;
